import type {
  Address,
  CheckoutForDelivery,
  Consignment,
  DeliveryMethod,
  DeliveryOption,
  HomeDeliveryAddress,
  PickupPointDeliveryAddress,
  ProductCheckout,
  ProductSelectionsRecord,
} from './checkout-for-delivery'
import {
  type CheckoutForDelivery as CheckoutForDeliveryResponse,
  type DeliveryOptions,
  type HomeDeliveryAddresses,
  type LineItemsForDelivery,
  type PickupPointAddresses,
  type SetCheckoutDeliveryOptions,
  type SetCheckoutLineItemsDeliveryOptions,
} from '@invivodf/module-cart-b2ag-interfaces'
export const toCheckoutForDelivery = (checkoutData: CheckoutForDeliveryResponse): CheckoutForDelivery => {
  return {
    id: checkoutData.id,
    companyName: checkoutData.companyName,
    lineItemsCount: checkoutData.lineItemsCount,
    canSelectDeliveryMethodByProduct: checkoutData.canSelectDeliveryMethodByProduct,
    prices: {
      totalLineItemsPreDiscounts: checkoutData.prices.totalLineItemsPreDiscounts,
      totalLineItems: checkoutData.prices.totalLineItems,
      totalRpd: checkoutData.prices.totalRpd,
      discounts: checkoutData.prices.discounts.map((discount) => ({
        label: discount.label,
        price: discount.price,
      })),
      totalWithoutTax: checkoutData.prices.totalWithoutTax,
      totalPaymentDueDates: checkoutData.prices.totalPaymentDueDates,
      totalTax: checkoutData.prices.totalTax,
      totalWithTax: checkoutData.prices.totalWithTax,
    },
    consignments: checkoutData.consignments.map((consignment) => ({
      id: consignment.id,
      addresses: [],
      delivery: {
        mode: consignment.delivery.mode,
        methods: consignment.delivery.methods.map((method) => ({
          reference: method.reference,
          name: method.name,
          route: method.route,
          description: method.description,
          deliveryPeriod: method.deliveryPeriod,
        })),
        comment: consignment.delivery.comment,
        method: {
          reference: consignment.delivery.method.reference,
          wishedPeriod: consignment.delivery.method.wishedPeriod,
        },
        address: consignment.delivery.address
          ? 'schedule' in consignment.delivery.address
            ? (consignment.delivery.address as PickupPointDeliveryAddress)
            : (consignment.delivery.address as HomeDeliveryAddress)
          : undefined,
      },
      lineItems: consignment.lineItems.map((item) => ({
        imageURL: item.imageURL,
        name: item.name,
        quantity: item.quantity,
        prices: undefined,
        services: item.services.map((service) => ({
          name: service.name,
        })),
        analytics: {
          offerId: item.analytics.offer_id,
          itemName: item.analytics.item_name,
          itemId: item.analytics.item_id,
          itemBrand: item.analytics.item_brand,
          itemCategory: item.analytics.item_category,
          itemCategory2: item.analytics.item_category2,
          itemCategory3: item.analytics.item_category3,
          itemCategory4: item.analytics.item_category4,
          itemVariant: item.analytics.item_variant,
          variantPackaging: item.analytics.variant_packaging,
          price: item.analytics.price,
          quantity: item.analytics.quantity,
        },
      })),
    })),
  }
}

export const toAdresses = (data: HomeDeliveryAddresses | PickupPointAddresses): Address[] => {
  return data.map((address) => ({
    reference: (address as any)?.reference,
    name: address.name,
    line: address.line,
    city: address.city,
    postalCode: address.postalCode,
    state: address.state,
    schedule: (address as any).schedule,
  }))
}

export const toDeliveryOptions = (data: DeliveryOptions): DeliveryOption[] => {
  return data.map((option) => ({
    reference: option.reference,
    name: option.name,
    basePrice: {
      price: option.basePrice.price
        ? {
            value: option.basePrice.price?.value,
            currency: option.basePrice.price?.currency,
          }
        : undefined,
      details: option.basePrice.details
        ? option.basePrice.details.map((detail) => ({
            reference: detail.reference,
            price: detail.price,
          }))
        : undefined,
    },
    addons: option.addons.map((addon) => ({
      reference: addon.reference,
      price: addon.price,
    })),
    wishedDeliveryDateOption: option.wishedDeliveryDateOption,
    hasCommentOption: option.hasCommentOption,
    transporterId: option.transporterId,
    providerId: option.providerId,
  }))
}

const formatDeliveryOption = (option: DeliveryOption, comment?: string) => {
  return {
    reference: option.reference,
    basePrice: {
      price: {
        value: option.basePrice.price?.value ?? 0,
        currency: option.basePrice.price?.currency ?? 'EUR',
      },
      details:
        option.basePrice && option.basePrice.details
          ? option.basePrice.details.map((detail) => ({
              reference: detail.reference ?? '',
              price: {
                value: detail.price?.value ?? 0,
                currency: detail.price?.currency ?? 'EUR',
              },
            }))
          : [],
    },
    addons: option.addons.map((addon) => ({
      reference: addon.reference ?? '',
      price: {
        value: addon.price?.value ?? 0,
        currency: addon.price?.currency ?? 'EUR',
      },
    })),
    wishedDeliveryDateRange: option.wishedDeliveryDateRange
      ? {
          start: option.wishedDeliveryDateRange.start,
          end: option.wishedDeliveryDateRange.end,
        }
      : undefined,
    comment,
    transporterId: option.transporterId,
    providerId: option.providerId,
  }
}

export const toConsignmentsForRequest = (consignments: Consignment[]): SetCheckoutDeliveryOptions['consignments'] => {
  return consignments.map((consignment) => {
    const option: DeliveryOption = ((consignment.delivery.methods[0] as DeliveryMethod).options as DeliveryOption[])[0]!
    const formattedSelectedOption = formatDeliveryOption(option, consignment.delivery.comment)
    return {
      id: consignment.id,
      logisticOfferId: consignment.delivery.methods[0]!.reference,
      deliveryAddressId: (consignment.delivery.address as Address).reference,
      deliveryOption: formattedSelectedOption,
    }
  })
}

export const toProductsCheckout = (lineItems: LineItemsForDelivery): ProductCheckout[] => {
  return lineItems.map((item) => ({
    id: item.id,
    imageURL: item.imageURL,
    name: item.name,
    quantity: item.quantity,
    delivery: {
      methods: item.delivery.methods.map((method: any) => ({
        reference: method.reference,
        name: method.name,
        route: method.route,
        deliveryPeriod: method.deliveryPeriod
          ? {
              start: method.deliveryPeriod.start,
              end: method.deliveryPeriod.end,
            }
          : undefined,
        hasCommentOption: method.hasCommentOption,
        wishedDeliveryDateOption: method.wishedDeliveryDateOption
          ? {
              mode: method.wishedDeliveryDateOption.mode,
              availableDates: method.wishedDeliveryDateOption.availableDates || [],
              mandatory: method.wishedDeliveryDateOption.mandatory,
            }
          : undefined,
      })),
    },
    services: item.services || [],
  }))
}

export const toSetCheckoutLineItemsDeliveryOptions = (
  productSelections: ProductSelectionsRecord,
): SetCheckoutLineItemsDeliveryOptions => {
  return Object.values(productSelections)
}
